<template>
  <div class="layout-column">
    <div class="layout-row">
      <validation-provider tag="div"
                           :name="path"
                           :ref="path"
                           :rules="{required: isRequired}"
                           v-slot="{ errors }">
        <lx-alert-box v-show="errors[0] && isRequired" color="red" class="my-2 ml-2">
          {{ $t('ERROR_EMPTY_METASTASES_ARRAY') }}
        </lx-alert-box>
        <v-text-field v-model.lazy="model"
                      :id="path"
                      v-show="false"
        ></v-text-field>
      </validation-provider>
    </div>

    <div v-bind:style="{'z-index': '4'}" class="layout-align-start-center pa-2">
      <lx-btn :disabled="isDisabled"
              icon="add_circle"
              :color="compColor.primeColor"
              :iconColor="'white'"
              :tooltip="$t('FORMS_DIVERSE.ADD_NEW_DISTANT_METASTASES')"
              :label="$t('FORMS_DIVERSE.ADD_NEW_DISTANT_METASTASES')"
              iconSize="21"
              :width="'auto'"
              :height="35"
              classes="text-none"
              @click="addNewItem"
      ></lx-btn>
    </div>

    <v-divider v-bind:style="{'border-color':compColor.lightDivider}" v-if="model && model.length > 0"></v-divider>

    <div :key="index" v-for="(item, index) in model"
         class="layout-row pa-0 ma-2"
         :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
         style="border-radius:4px;"
         :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

      <div class="layout-row layout-wrap flex px-3">

        <!-- Fernmetastasedatum -->

        <div class="flex-gt-sm-33 flex-100 px-2 py-3">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.fm_diagnosedatum'"
                               :ref="path + '.' + index + '.fm_diagnosedatum'"
                               :rules="{required:true,german_date:true,date_not_in_future:true,date_must_be_lesser_than_death:[deathDate,item.fm_diagnosedatum_genauigkeit]}"
                               v-slot="{ errors }">
            <lx-datepicker
                :color="compColor.textInput"
                :name="path + '.' + index + '.fm_diagnosedatum'"
                :label="$t('FORMS_DIVERSE.DISTANT_METASTASES_DATE') + ' *'"
                :dense="denseFields"
                @input="setDateAccuracyAfterDate(index)"
                :error="errors[0]"
                :hide-details="!errors || errors.length <= 0"
                :disabled="isDisabled || (disableManuallyValidate && !isAdditionalImportForm && index === 0)"
                v-model="item.fm_diagnosedatum"
            ></lx-datepicker>
          </validation-provider>
        </div>

        <!-- Datumsgenauigkeit Fernmetastasedatum -->

        <div class="flex-gt-sm-33 flex-100 px-2 py-3 layout-row">
          <validation-provider tag="div"
                               class="flex"
                               :name="path + '.' + index + '.fm_diagnosedatum_genauigkeit'"
                               :ref="path + '.' + index + '.fm_diagnosedatum_genauigkeit'"
                               rules="required"
                               v-slot="{ errors }">
            <v-select
              :color="compColor.primeColor"
              :label="$t('FORMS_DIVERSE.DISTANT_METASTASES_DATE_ACCURACY') + ' *'"
              v-model="item.fm_diagnosedatum_genauigkeit"
              :items="DateAccuracyTypes"
              :disabled="isDisabled"
              outlined
              class="mb-0"
              @change="validateDateAfterChangeOfAccuracy(index)"
              :hide-details="!errors || errors.length <= 0"
              :dense="denseFields"
              :error-messages="errors[0]"
              :menu-props="{ bottom: true, offsetY: true }"
              :dark="compColor.isInDarkMode">
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on"
                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                  <v-list-item-content class="py-0">
                    <label style="cursor:pointer;"
                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </validation-provider>

          <v-tooltip bottom open-delay="1000">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 mr-3 ml-2"
                   :class="{'mt-1': !denseFields}"
                   style="cursor: pointer;">
                <v-icon :color="compColor.prime" medium>info</v-icon>
              </div>
            </template>
            <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.TOOLTIP_DATE_ACCURACY') }}</p>
          </v-tooltip>
        </div>

        <!-- Seitenlokalisation -->

        <div class="flex-gt-sm-33 flex-100 px-2 py-3">
          <validation-provider tag="div"
                               :name="path + '.' + index + '.fm_lokalisation'"
                               :ref="path + '.' + index + '.fm_lokalisation'"
                               rules="required"
                               v-slot="{ errors }">
            <v-select
                :color="compColor.primeColor"
                :label="$t('FORMS_DIVERSE.DISTANT_METASTASES_LOCALIZATION') + ' *'"
                v-model="item.fm_lokalisation"
                :items="localisationTypes"
                outlined
                :hide-details="!errors || errors.length <= 0"
                :disabled="isDisabled || (disableManuallyValidate && !isAdditionalImportForm && index === 0)"
                :dense="denseFields"
                class="ma-0"
                :error-messages="errors[0]"
                :menu-props="{ bottom: true, offsetY: true }"
                :dark="compColor.isInDarkMode">
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on"
                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                  <v-list-item-content class="py-0">
                    <label style="cursor:pointer;"
                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </validation-provider>
        </div>
      </div>

      <div class="layout-column layout-align-center-center px-3"
           :class="{'grey lighten-2': !compColor.isInDarkMode}"
           :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
        <lx-btn @click="deleteMetastasis(path, index, path + '.' + index + '.fm_lokalisation')"
                :color="compColor.primeColor"
                :iconColor="'white'"
                iconSize="21"
                :width="35"
                :height="35"
                :disabled="isDisabled || (disableManuallyValidate && !isAdditionalImportForm && index === 0) || (isRequired && model.length === 1)" icon="delete_forever"></lx-btn>
      </div>
    </div>
  </div>
</template>

<script>
import metaData from '../shared/formsMetaData'

export default {
  name: "Fernmetastase",
  mixins: [metaData],
  props: {
    value: {
      type: Array
    },
    path: {
      type: String
    },
    addItem: {
      type: Function
    },
    deleteItem: {
      type: Function
    },
    isDisabled: {
      type: Boolean
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    disableManuallyValidate: {
      type: Boolean
    },
    isAdditionalImportForm: {
      type: Boolean
    },
    leadDate: {
      type: [Object, String]
    },
    dateOfDeath: {
      type: [Object, String]
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    patientId: {
      type: String
    }
  },
  data () {
    return {
      model: [],
      localisationTypes: null,
      DateAccuracyTypes: null,
    }
  },
  created () {
    this.localisationTypes = this.formsMetaData.FM_LokalisationTypes
    this.DateAccuracyTypes = this.formsMetaData.DateAccuracyLong
  },
  methods: {
    /**
     * Validate date after changing date accuracy
     *
     * @param index The index of selected classification
     */
    validateDateAfterChangeOfAccuracy (index) {
      this.$nextTick(() => {
        if (this.$refs[this.path + '.' + index + '.fm_diagnosedatum'] && this.$refs[this.path + '.' + index + '.fm_diagnosedatum'][0]) {
          this.$refs[this.path + '.' + index + '.fm_diagnosedatum'][0].validate()
        }
      })
    },

    /**
     * Set date accuracy per default after date input
     *
     * @param index
     */
    setDateAccuracyAfterDate (index) {
      // set fm_diagnosedatum_genauigkeit if not exists after changing fm_diagnosedatum in datepicker
      if (this.model[index] && this.model[index].fm_diagnosedatum && !this.model[index].fm_diagnosedatum_genauigkeit) {
        this.model[index].fm_diagnosedatum_genauigkeit = 'E'
      }
    },

    /**
     * Add new item as object to array
     */
    addNewItem () {
      let newValue = {}

      if (this.leadDate) {
        newValue.fm_diagnosedatum = this.leadDate
        newValue.fm_diagnosedatum_genauigkeit = 'E'
      }

      this.addItem(this.path, newValue)

      // trigger validator only in documentation and not in import
      if(!this.disableManuallyValidate && this.validationRef.fields && this.validationRef.fields[this.path]) {
        this.validationRef.fields[this.path].changed = true
      }
    },

    /**
     * Delete selected element of metastasis and trigger validator for saving
     */
    deleteMetastasis (path, index, field) {
      this.deleteItem(path, index)

      // trigger validator only in documentation and not in import
      if(!this.disableManuallyValidate) {
        this.validationRef.fields[field].changed = true
      }
    }
  },
  computed: {
    deathDate () {
      // props with date comes from import
      if (this.dateOfDeath) {
        return this.dateOfDeath
      }

      return this.patientId ? this.$store.getters.diagnosisBaseData(this.patientId).deathDate : undefined
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
        }
      },
      immediate: true
    }
  }
}
</script>
